class ImageMarquee {
  constructor(container) {
    this.container = container;
    this.layoutType = container.dataset.layout;
    this.horizontalStyle = container.dataset.horizontalStyle;
    this.angledStyle = container.dataset.angledStyle;
    this.speed = parseFloat(container.dataset.speed) || 30;
    this.tracks = container.querySelectorAll('.marquee-track');
    this.isVisible = false;
    this.observer = null;
    
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupAnimations();
    this.setupResponsiveHandler();
    this.handleMotionPreference();
    this.setupProductHoverEffects();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isVisible = true;
          this.startAnimations();
        } else {
          this.isVisible = false;
          this.pauseAnimations();
        }
      });
    }, options);

    this.observer.observe(this.container);
  }

  setupAnimations() {
    this.tracks.forEach((track, index) => {
      const direction = track.dataset.direction || 'left';
      
      track.style.animationDuration = `${this.speed}s`;
      track.style.animationName = direction === 'right' 
        ? 'marquee-right' 
        : 'marquee-left';
    });
  }

  startAnimations() {
    this.tracks.forEach(track => {
      track.style.animationPlayState = 'running';
    });
  }

  pauseAnimations() {
    this.tracks.forEach(track => {
      track.style.animationPlayState = 'paused';
    });
  }

  setupResponsiveHandler() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  handleResize() {
    // Recalculate animations on resize if needed
    this.setupAnimations();
  }

  handleMotionPreference() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const updateMotion = (mediaQuery) => {
      if (mediaQuery.matches) {
        this.pauseAnimations();
        this.container.classList.add('motion-reduced');
      } else {
        if (this.isVisible) {
          this.startAnimations();
        }
        this.container.classList.remove('motion-reduced');
      }
    };

    // Initial check
    updateMotion(prefersReducedMotion);
    
    // Listen for changes
    prefersReducedMotion.addEventListener('change', updateMotion);
  }

  updateSpeed(newSpeed) {
    this.speed = newSpeed;
    this.setupAnimations();
  }



  setupProductHoverEffects() {
    const marqueeItems = this.container.querySelectorAll('[data-has-product="true"]');
    const marqueeWrappers = this.container.querySelectorAll('.marquee-wrapper');
    
    marqueeItems.forEach(item => {
      const tooltip = item.querySelector('.product-tooltip');
      if (!tooltip) return;
      
      let tooltipLocked = false;
      let lockedPosition = null;
      
      item.addEventListener('mouseenter', (e) => {
        this.pauseAnimations();
        // Push all other wrappers behind
        marqueeWrappers.forEach(wrapper => {
          if (wrapper !== item.closest('.marquee-wrapper')) {
            wrapper.style.zIndex = '-1';
          }
        });
        
        // Lock tooltip position on first hover
        if (!tooltipLocked) {
          const rect = item.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          lockedPosition = mouseX;
          tooltip.style.left = mouseX + 'px';
          tooltipLocked = true;
        }
      });
      
      item.addEventListener('mousemove', (e) => {
        if (tooltip && !tooltipLocked) {
          // Only update position if not locked
          const rect = item.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          tooltip.style.left = mouseX + 'px';
        }
      });
      
      item.addEventListener('mouseleave', () => {
        this.startAnimations();
        // Reset all wrappers to normal z-index
        marqueeWrappers.forEach(wrapper => {
          wrapper.style.zIndex = '';
        });
        
        // Reset tooltip lock state for next hover
        tooltipLocked = false;
        lockedPosition = null;
      });
      
      // Make tooltip clickable and keyboard-accessible
      const navigateToProduct = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productUrl = item.querySelector('.product-tooltip__view-link')?.href;
        if (productUrl) {
          window.location.href = productUrl;
        }
      };
      tooltip.addEventListener('click', navigateToProduct);
      tooltip.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') navigateToProduct(e);
      });
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Clean up product hover listeners
    const productItems = this.container.querySelectorAll('.marquee-item[data-has-product="true"]');
    productItems.forEach(item => {
      item.removeEventListener('mouseenter', this.pauseAnimations);
      item.removeEventListener('mouseleave', this.startAnimations);
    });
  }
}

// Initialize all image marquee sections
document.addEventListener('DOMContentLoaded', () => {
  const marqueeContainers = document.querySelectorAll('.image-marquee-container');
  const marqueeInstances = [];

  marqueeContainers.forEach(container => {
    const instance = new ImageMarquee(container);
    marqueeInstances.push(instance);
  });

  // Store instances globally for potential theme editor integration
  window.imageMarqueeInstances = marqueeInstances;
});

// Theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.image-marquee-container');
    if (container) {
      const instance = new ImageMarquee(container);
      if (!window.imageMarqueeInstances) {
        window.imageMarqueeInstances = [];
      }
      window.imageMarqueeInstances.push(instance);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.image-marquee-container');
    if (container && window.imageMarqueeInstances) {
      const index = window.imageMarqueeInstances.findIndex(
        instance => instance.container === container
      );
      if (index !== -1) {
        window.imageMarqueeInstances[index].destroy();
        window.imageMarqueeInstances.splice(index, 1);
      }
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const container = event.target.querySelector('.image-marquee-container');
    if (container) {
      // Pause animation when section is selected in theme editor
      const tracks = container.querySelectorAll('.marquee-track');
      tracks.forEach(track => {
        track.style.animationPlayState = 'paused';
      });
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const container = event.target.querySelector('.image-marquee-container');
    if (container) {
      // Resume animation when section is deselected
      const tracks = container.querySelectorAll('.marquee-track');
      tracks.forEach(track => {
        track.style.animationPlayState = 'running';
      });
    }
  });
}

// Utility function to create smooth transitions between different marquee configurations
function transitionMarqueeLayout(container, newConfig) {
  const instance = window.imageMarqueeInstances?.find(
    inst => inst.container === container
  );
  
  if (instance) {
    // Fade out
    container.style.transition = 'opacity 0.3s ease';
    container.style.opacity = '0';
    
    setTimeout(() => {
      // Update configuration
      Object.assign(instance, newConfig);
      instance.setupAnimations();
      
      // Fade in
      container.style.opacity = '1';
      
      // Clean up transition
      setTimeout(() => {
        container.style.transition = '';
      }, 300);
    }, 300);
  }
}

// Export for potential external usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ImageMarquee, transitionMarqueeLayout };
}

